import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

// 获取用户特定的存储键
const getSearchTourStorageKey = (userId) => {
	if (!userId) return null;
	return `hasSeenSearchTour_${userId}`;
};

const SearchTourGuide = forwardRef((props, ref) => {
	const { userInfo } = useSelector((state) => state.auth);
	const [showTour, setShowTour] = useState(false);
	const [step, setStep] = useState(0);
	const searchBoxRef = useRef(null);

	// 暴露方法给父组件
	useImperativeHandle(ref, () => ({
		show: () => {
			// 查找搜索框元素
			const searchInput = document.querySelector('.search-input');
			if (searchInput) {
				searchBoxRef.current = searchInput;
			}
			setStep(0);
			setShowTour(true);
		},
	}));

	useEffect(() => {
		// 只有在用户已登录的情况下才显示指引
		if (!userInfo || !userInfo._id) return;

		// 获取用户特定的存储键
		const storageKey = getSearchTourStorageKey(userInfo._id);
		if (!storageKey) return;

		// 检查该用户是否已经看过指引
		const hasSeenTour = localStorage.getItem(storageKey);
		if (!hasSeenTour) {
			// 延迟显示，确保页面已加载
			const timer = setTimeout(() => {
				// 查找搜索框元素
				const searchInput = document.querySelector('.search-input');
				if (searchInput) {
					searchBoxRef.current = searchInput;
					setShowTour(true);
				}
			}, 1500); // 延迟1.5秒，让页面完全加载

			return () => clearTimeout(timer);
		}
	}, [userInfo]);

	const handleClose = useCallback(() => {
		setShowTour(false);
		// 保存该用户的指引记录
		if (userInfo && userInfo._id) {
			const storageKey = getSearchTourStorageKey(userInfo._id);
			if (storageKey) {
				localStorage.setItem(storageKey, 'true');
			}
		}
	}, [userInfo]);

	const handleNext = useCallback(() => {
		setStep((prevStep) => prevStep + 1);
	}, []);

	const handlePrev = useCallback(() => {
		setStep((prevStep) => prevStep - 1);
	}, []);

	const handleStart = useCallback(() => {
		handleClose();
		// 聚焦搜索框
		if (searchBoxRef.current) {
			searchBoxRef.current.focus();
		}
	}, [handleClose]);

	// 键盘导航支持
	useEffect(() => {
		if (!showTour) return;

		const handleKeyDown = (e) => {
			// 左右箭头键切换步骤
			if (e.key === 'ArrowLeft' && step > 0) {
				e.preventDefault();
				setStep(step - 1);
			} else if (e.key === 'ArrowRight' && step < 2) {
				e.preventDefault();
				setStep(step + 1);
			} else if (e.key === 'Escape') {
				e.preventDefault();
				handleClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [showTour, step, handleClose]);

	if (!showTour) return null;

	const steps = [
		{
			icon: 'search',
			title: '全局搜索',
			subtitle: '快速查找订单、客户和产品',
			content: (
				<div className='tour-content'>
					<div className='feature-list'>
						<div className='feature-item'>
							<div className='feature-icon'>
								<FontAwesomeIcon icon='bolt' />
							</div>
							<div className='feature-text'>
								<div className='feature-title'>快速搜索</div>
								<div className='feature-desc'>支持订单编号、客户名称、产品名称等多种搜索方式，实时显示搜索结果</div>
							</div>
						</div>
						<div className='feature-item'>
							<div className='feature-icon'>
								<FontAwesomeIcon icon='key' />
							</div>
							<div className='feature-text'>
								<div className='feature-title'>快捷键</div>
								<div className='feature-desc'>
									<span className='keyboard-shortcut'>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘' : 'Ctrl'}K</span>
									快速聚焦搜索框，随时随地开始搜索
								</div>
							</div>
						</div>
						<div className='feature-item'>
							<div className='feature-icon'>
								<FontAwesomeIcon icon='clock-rotate-left' />
							</div>
							<div className='feature-text'>
								<div className='feature-title'>搜索历史</div>
								<div className='feature-desc'>自动保存最近访问的订单、客户和产品，支持列表和宫格两种视图切换</div>
							</div>
						</div>
						<div className='feature-item'>
							<div className='feature-icon'>
								<FontAwesomeIcon icon='window-maximize' />
							</div>
							<div className='feature-text'>
								<div className='feature-title'>详情展示</div>
								<div className='feature-desc'>点击搜索结果可在弹窗中查看详细信息，无需跳转页面</div>
							</div>
						</div>
					</div>
				</div>
			),
		},
		{
			icon: 'key',
			title: '键盘导航',
			subtitle: '使用键盘快速操作',
			content: (
				<div className='tour-content'>
					<div className='keyboard-guide'>
						<div className='keyboard-item'>
							<div className='keyboard-keys'>
								<kbd>↑</kbd>
								<kbd>↓</kbd>
								<kbd>←</kbd>
								<kbd>→</kbd>
							</div>
							<div className='keyboard-desc'>箭头键导航搜索结果（宫格视图支持上下左右，列表视图支持上下）</div>
						</div>
						<div className='keyboard-item'>
							<div className='keyboard-keys'>
								<kbd>Enter</kbd>
							</div>
							<div className='keyboard-desc'>打开选中项的详细信息（订单打开处理窗口，客户/产品打开详情弹窗）</div>
						</div>
						<div className='keyboard-item'>
							<div className='keyboard-keys'>
								<kbd>Esc</kbd>
							</div>
							<div className='keyboard-desc'>关闭搜索卡片，返回首页</div>
						</div>
						<div className='keyboard-item'>
							<div className='keyboard-keys'>
								{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? (
									<>
										<kbd>⌘</kbd>
										<kbd>K</kbd>
									</>
								) : (
									<>
										<kbd>Ctrl</kbd>
										<kbd>K</kbd>
									</>
								)}
							</div>
							<div className='keyboard-desc'>快速聚焦搜索框并打开搜索卡片</div>
						</div>
					</div>
					<div className='tip-box'>
						<FontAwesomeIcon icon='info-circle' className='tip-icon' />
						<div className='tip-content'>
							<div className='tip-title'>提示</div>
							<div className='tip-text'>在宫格视图下，箭头键会按照视觉位置导航（上下左右）；在列表视图下，箭头键按顺序导航</div>
						</div>
					</div>
				</div>
			),
		},
		{
			icon: 'rocket',
			title: '开始使用',
			subtitle: '立即体验强大的搜索功能',
			content: (
				<div className='tour-content'>
					<div className='start-guide'>
						<div className='start-step'>
							<div className='step-number'>1</div>
							<div className='step-content'>
								<div className='step-title'>聚焦搜索框</div>
								<div className='step-desc'>按 {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘K' : 'Ctrl+K'} 或点击搜索框，打开搜索卡片</div>
							</div>
						</div>
						<div className='start-step'>
							<div className='step-number'>2</div>
							<div className='step-content'>
								<div className='step-title'>输入关键词</div>
								<div className='step-desc'>输入至少 2 个字符，系统会实时显示匹配的订单、客户和产品</div>
							</div>
						</div>
						<div className='start-step'>
							<div className='step-number'>3</div>
							<div className='step-content'>
								<div className='step-title'>导航和选择</div>
								<div className='step-desc'>使用箭头键浏览结果，按 Enter 打开详情，或直接点击查看</div>
							</div>
						</div>
					</div>
					<div className='highlight-box'>
						<FontAwesomeIcon icon='star' className='highlight-icon' />
						<div className='highlight-text'>搜索历史会自动保存，支持列表和宫格两种视图切换，方便快速访问常用内容</div>
					</div>
				</div>
			),
		},
	];

	const currentStep = steps[step];

	return (
		<>
			{/* 遮罩层 */}
			{showTour && (
				<div
					className='search-tour-overlay'
					onClick={handleClose}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						backdropFilter: 'blur(8px)',
						WebkitBackdropFilter: 'blur(8px)',
						zIndex: 1060,
						transition: 'opacity 0.3s ease-in-out',
					}}
				/>
			)}

			{/* 指引弹窗 */}
			<Modal show={showTour} onHide={handleClose} centered size='lg' className='search-tour-modal tour-modal-entering' style={{ zIndex: 1070 }} backdrop={false}>
				<Modal.Header
					className='tour-modal-header'
					style={{
						borderBottom: 'none',
						padding: '2rem 2rem 1.5rem',
						position: 'relative',
					}}
				>
					<div className='d-flex align-items-center w-100'>
						<div className='tour-icon-wrapper tour-icon-animate'>
							<FontAwesomeIcon icon={currentStep.icon} className='tour-icon' />
						</div>
						<div className='flex-grow-1 ms-3'>
							<Modal.Title className='tour-title mb-1 tour-title-animate'>{currentStep.title}</Modal.Title>
							<div className='tour-subtitle tour-subtitle-animate'>{currentStep.subtitle}</div>
						</div>
						<Button
							variant='link'
							className='tour-close-btn'
							onClick={handleClose}
							style={{
								padding: '0.5rem',
								color: 'var(--bs-gray-500)',
								opacity: 0.7,
								transition: 'all 0.2s ease',
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.opacity = '1';
								e.currentTarget.style.color = 'var(--bs-gray-700)';
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.opacity = '0.7';
								e.currentTarget.style.color = 'var(--bs-gray-500)';
							}}
						>
							<FontAwesomeIcon icon='times' />
						</Button>
					</div>
				</Modal.Header>
				<Modal.Body className='tour-modal-body tour-content-animate' style={{ padding: '0 2rem 1.5rem' }}>
					{/* 进度条 */}
					<div className='tour-progress-container' style={{ marginBottom: '1.5rem' }}>
						<div className='tour-progress-bar'>
							<div
								className='tour-progress-fill'
								style={{
									width: `${((step + 1) / steps.length) * 100}%`,
									transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
								}}
							/>
						</div>
						<div className='tour-progress-text' style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--bs-gray-600)', textAlign: 'right' }}>
							{step + 1} / {steps.length}
						</div>
					</div>
					{currentStep.content}
				</Modal.Body>
				<Modal.Footer className='tour-modal-footer' style={{ borderTop: 'none', padding: '1.5rem 2rem 2rem' }}>
					<div className='d-flex justify-content-between align-items-center w-100'>
						{/* 步骤指示器 */}
						<div className='d-flex align-items-center'>
							{steps.map((_, index) => (
								<button
									key={index}
									className='step-indicator'
									data-current={index === step}
									onClick={() => setStep(index)}
									aria-label={`步骤 ${index + 1}`}
									style={{
										width: index === step ? '32px' : '8px',
										height: '8px',
										borderRadius: '4px',
										background: index === step ? 'linear-gradient(135deg, var(--app-theme-primary-light) 0%, var(--app-theme-primary) 50%, var(--app-theme-primary-hover) 100%)' : 'rgba(var(--app-theme-primary-rgb), 0.3)',
										border: index === step ? '1px solid rgba(var(--app-theme-primary-rgb), 0.4)' : '1px solid rgba(var(--app-theme-primary-rgb), 0.2)',
										boxShadow: index === step ? '0 2px 8px rgba(var(--app-theme-primary-rgb), 0.4), 0 0 12px rgba(var(--app-theme-primary-rgb), 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
										padding: 0,
										marginRight: index < steps.length - 1 ? '0.5rem' : 0,
										cursor: 'pointer',
										transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
										position: 'relative',
									}}
								/>
							))}
						</div>

						{/* 导航按钮 */}
						<div className='d-flex gap-2'>
							{step > 0 && (
								<Button
									variant='outline-secondary'
									onClick={handlePrev}
									className='tour-nav-btn'
									style={{
										borderRadius: '8px',
										padding: '0.5rem 1.25rem',
										fontWeight: 500,
										border: '1.5px solid rgba(var(--app-theme-primary-rgb), 0.2)',
										backgroundColor: 'rgba(var(--app-theme-primary-rgb), 0.05)',
										color: 'var(--app-theme-primary)',
										transition: 'all 0.2s ease',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.borderColor = 'rgba(var(--app-theme-primary-rgb), 0.4)';
										e.currentTarget.style.backgroundColor = 'rgba(var(--app-theme-primary-rgb), 0.1)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.borderColor = 'rgba(var(--app-theme-primary-rgb), 0.2)';
										e.currentTarget.style.backgroundColor = 'rgba(var(--app-theme-primary-rgb), 0.05)';
									}}
								>
									<FontAwesomeIcon icon='chevron-left' className='me-2' />
									上一步
								</Button>
							)}
							{step < steps.length - 1 ? (
								<Button
									variant='primary'
									onClick={handleNext}
									className='tour-nav-btn'
									style={{
										borderRadius: '8px',
										padding: '0.5rem 1.5rem',
										fontWeight: 500,
										background: 'linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.9) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.9) 100%)',
										border: '1.5px solid rgba(var(--app-theme-primary-rgb), 0.3)',
										boxShadow: '0 2px 8px rgba(var(--app-theme-primary-rgb), 0.25)',
										transition: 'all 0.2s ease',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.transform = 'translateY(-1px)';
										e.currentTarget.style.background = 'linear-gradient(135deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%)';
										e.currentTarget.style.boxShadow = '0 4px 12px rgba(var(--app-theme-primary-rgb), 0.35)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.transform = 'translateY(0)';
										e.currentTarget.style.background = 'linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.9) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.9) 100%)';
										e.currentTarget.style.boxShadow = '0 2px 8px rgba(var(--app-theme-primary-rgb), 0.25)';
									}}
								>
									下一步
									<FontAwesomeIcon icon='chevron-right' className='ms-2' />
								</Button>
							) : (
								<Button
									variant='primary'
									onClick={handleStart}
									className='tour-nav-btn'
									style={{
										borderRadius: '8px',
										padding: '0.5rem 1.5rem',
										fontWeight: 500,
										background: 'linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.9) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.9) 100%)',
										border: '1.5px solid rgba(var(--app-theme-primary-rgb), 0.3)',
										boxShadow: '0 2px 8px rgba(var(--app-theme-primary-rgb), 0.25)',
										transition: 'all 0.2s ease',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.transform = 'translateY(-1px)';
										e.currentTarget.style.background = 'linear-gradient(135deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%)';
										e.currentTarget.style.boxShadow = '0 4px 12px rgba(var(--app-theme-primary-rgb), 0.35)';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.transform = 'translateY(0)';
										e.currentTarget.style.background = 'linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.9) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.9) 100%)';
										e.currentTarget.style.boxShadow = '0 2px 8px rgba(var(--app-theme-primary-rgb), 0.25)';
									}}
								>
									<FontAwesomeIcon icon='search' className='me-2' />
									开始搜索
								</Button>
							)}
						</div>
					</div>
				</Modal.Footer>
			</Modal>

			<style>{`
				@keyframes tourModalEnter {
					from {
						opacity: 0;
						transform: scale(0.95) translateY(-20px);
					}
					to {
						opacity: 1;
						transform: scale(1) translateY(0);
					}
				}

				@keyframes tourIconPulse {
					0%, 100% {
						transform: scale(1);
					}
					50% {
						transform: scale(1.05);
					}
				}

				@keyframes tourContentFadeIn {
					from {
						opacity: 0;
						transform: translateY(10px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}

				@keyframes tourFeatureSlide {
					from {
						opacity: 0;
						transform: translateX(-20px);
					}
					to {
						opacity: 1;
						transform: translateX(0);
					}
				}

				.search-tour-modal.tour-modal-entering .modal-content {
					animation: tourModalEnter 0.4s cubic-bezier(0.4, 0, 0.2, 1);
				}

				.search-tour-modal .modal-content {
					border: none;
					border-radius: 16px;
					box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
					overflow: hidden;
					position: relative;
				}

				.search-tour-modal .modal-content::before {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					height: 4px;
					background: linear-gradient(90deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 50%, var(--app-theme-primary) 100%);
					background-size: 200% 100%;
					animation: tourProgressLine 2s linear infinite;
				}

				@keyframes tourProgressLine {
					0% {
						background-position: 0% 0%;
					}
					100% {
						background-position: 200% 0%;
					}
				}

				.tour-modal-header {
					background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
				}

				.tour-icon-wrapper {
					width: 56px;
					height: 56px;
					border-radius: 14px;
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.15) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.15) 100%);
					border: 2px solid rgba(var(--app-theme-primary-rgb), 0.2);
					display: flex;
					align-items: center;
					justify-content: center;
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.1);
					position: relative;
					overflow: hidden;
				}

				.tour-icon-wrapper::before {
					content: '';
					position: absolute;
					top: -50%;
					left: -50%;
					width: 200%;
					height: 200%;
					background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
					animation: tourIconShine 3s ease-in-out infinite;
				}

				@keyframes tourIconShine {
					0%, 100% {
						transform: translate(-50%, -50%) rotate(0deg);
					}
					50% {
						transform: translate(-50%, -50%) rotate(180deg);
					}
				}

				.tour-icon-animate {
					animation: tourIconPulse 2s ease-in-out infinite;
				}

				.tour-icon {
					font-size: 24px;
					color: var(--app-theme-primary) !important;
					display: block !important;
					visibility: visible !important;
				}

				.tour-title {
					font-size: 1.5rem;
					font-weight: 600;
					color: var(--app-theme-primary) !important;
					line-height: 1.2;
					background: linear-gradient(135deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%);
					-webkit-background-clip: text;
					-webkit-text-fill-color: transparent;
					background-clip: text;
				}

				.tour-title-animate {
					animation: tourContentFadeIn 0.5s ease-out 0.1s both;
				}

				.tour-subtitle {
					font-size: 0.875rem;
					color: var(--bs-gray-600);
					font-weight: 400;
				}

				.tour-subtitle-animate {
					animation: tourContentFadeIn 0.5s ease-out 0.2s both;
				}

				.tour-close-btn {
					display: flex !important;
					align-items: center !important;
					justify-content: center !important;
					visibility: visible !important;
					min-width: 32px;
					min-height: 32px;
				}

				.tour-close-btn svg {
					display: block !important;
					visibility: visible !important;
					width: 18px !important;
					height: 18px !important;
					color: var(--bs-gray-500) !important;
				}

				.tour-close-btn:hover svg {
					color: var(--bs-gray-700) !important;
				}

				.tour-content {
					padding: 0.5rem 0;
				}

				.tour-content-animate {
					animation: tourContentFadeIn 0.5s ease-out 0.3s both;
				}

				.tour-progress-container {
					margin-bottom: 1.5rem;
				}

				.tour-progress-bar {
					width: 100%;
					height: 4px;
					background: var(--bs-gray-200);
					border-radius: 2px;
					overflow: hidden;
					position: relative;
				}

				.tour-progress-fill {
					height: 100%;
					background: linear-gradient(90deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%);
					border-radius: 2px;
					position: relative;
					overflow: hidden;
				}

				.tour-progress-fill::after {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					bottom: 0;
					right: 0;
					background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
					background-size: 200% 100%;
					animation: tourProgressShine 2s linear infinite;
				}

				@keyframes tourProgressShine {
					0% {
						background-position: -200% 0;
					}
					100% {
						background-position: 200% 0;
					}
				}

				.tour-progress-text {
					margin-top: 0.5rem;
					font-size: 0.75rem;
					color: var(--bs-gray-600);
					text-align: right;
					font-weight: 500;
				}

				.feature-list {
					display: flex;
					flex-direction: column;
					gap: 1.25rem;
				}

				.feature-item {
					display: flex;
					align-items: flex-start;
					gap: 1rem;
					padding: 1rem;
					border-radius: 12px;
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.03) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.03) 100%);
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					border: 1.5px solid rgba(var(--app-theme-primary-rgb), 0.1);
					position: relative;
					overflow: hidden;
				}

				.feature-item::before {
					content: '';
					position: absolute;
					left: 0;
					top: 0;
					bottom: 0;
					width: 0;
					background: linear-gradient(180deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%);
					transition: width 0.3s ease;
				}

				.feature-item:hover {
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.08) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.08) 100%);
					transform: translateX(4px);
					border-color: rgba(var(--app-theme-primary-rgb), 0.3);
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.15);
				}

				.feature-item:hover::before {
					width: 3px;
				}

				.feature-item:nth-child(1) {
					animation: tourFeatureSlide 0.5s ease-out 0.4s both;
				}

				.feature-item:nth-child(2) {
					animation: tourFeatureSlide 0.5s ease-out 0.5s both;
				}

				.feature-item:nth-child(3) {
					animation: tourFeatureSlide 0.5s ease-out 0.6s both;
				}

				.feature-item:nth-child(4) {
					animation: tourFeatureSlide 0.5s ease-out 0.7s both;
				}

				.feature-icon {
					width: 40px;
					height: 40px;
					border-radius: 10px;
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.12) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.12) 100%);
					border: 1.5px solid rgba(var(--app-theme-primary-rgb), 0.2);
					display: flex !important;
					align-items: center;
					justify-content: center;
					color: var(--app-theme-primary) !important;
					font-size: 18px;
					flex-shrink: 0;
					transition: all 0.3s ease;
					position: relative;
					overflow: hidden;
				}

				.feature-icon svg {
					display: block !important;
					visibility: visible !important;
					color: var(--app-theme-primary) !important;
					width: 18px !important;
					height: 18px !important;
				}

				.feature-item:hover .feature-icon {
					transform: scale(1.1) rotate(5deg);
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.2) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.2) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.4);
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.2);
				}

				.feature-icon::after {
					content: '';
					position: absolute;
					top: 50%;
					left: 50%;
					width: 0;
					height: 0;
					border-radius: 50%;
					background: rgba(255, 255, 255, 0.3);
					transform: translate(-50%, -50%);
					transition: width 0.4s ease, height 0.4s ease;
				}

				.feature-item:hover .feature-icon::after {
					width: 100%;
					height: 100%;
				}

				.feature-text {
					flex: 1;
				}

				.feature-title {
					font-size: 0.9375rem;
					font-weight: 600;
					color: var(--bs-gray-900);
					margin-bottom: 0.25rem;
				}

				.feature-desc {
					font-size: 0.875rem;
					color: var(--bs-gray-600);
					line-height: 1.5;
				}

				.keyboard-shortcut {
					display: inline-block;
					padding: 0.25rem 0.625rem;
					margin: 0 0.375rem;
					background: linear-gradient(135deg, var(--bs-gray-200) 0%, var(--bs-gray-300) 100%);
					border-radius: 6px;
					font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
					font-size: 0.75rem;
					font-weight: 600;
					color: var(--bs-gray-700);
					border: 1px solid var(--bs-gray-300);
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1);
					transition: all 0.2s ease;
				}

				.keyboard-shortcut:hover {
					background: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary-dark, #0056b3) 100%);
					color: white;
					border-color: var(--app-theme-primary);
					transform: translateY(-1px);
					box-shadow: 0 2px 6px rgba(var(--app-theme-primary-rgb), 0.3);
				}

				.keyboard-guide {
					display: flex;
					flex-direction: column;
					gap: 1rem;
				}

				.keyboard-item {
					display: flex;
					align-items: center;
					gap: 1rem;
					padding: 1rem;
					border-radius: 12px;
					background: var(--bs-gray-50);
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					border: 1.5px solid transparent;
				}

				.keyboard-item:hover {
					background: var(--bs-gray-100);
					border-color: var(--app-theme-primary);
					transform: translateX(4px);
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.1);
				}

				.keyboard-item:nth-child(1) {
					animation: tourFeatureSlide 0.5s ease-out 0.4s both;
				}

				.keyboard-item:nth-child(2) {
					animation: tourFeatureSlide 0.5s ease-out 0.5s both;
				}

				.keyboard-item:nth-child(3) {
					animation: tourFeatureSlide 0.5s ease-out 0.6s both;
				}

				.keyboard-item:nth-child(4) {
					animation: tourFeatureSlide 0.5s ease-out 0.7s both;
				}

				.keyboard-keys {
					display: flex;
					gap: 0.5rem;
					align-items: center;
				}

				.keyboard-keys kbd {
					display: inline-block;
					padding: 0.375rem 0.75rem;
					background: white;
					border: 1.5px solid var(--bs-gray-300);
					border-radius: 6px;
					font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
					font-size: 0.8125rem;
					font-weight: 600;
					color: var(--bs-gray-700);
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
					min-width: 32px;
					text-align: center;
					transition: all 0.2s ease;
					position: relative;
				}

				.keyboard-item:hover .keyboard-keys kbd {
					transform: translateY(-2px);
					box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.15);
					border-color: var(--app-theme-primary);
				}

				.keyboard-desc {
					font-size: 0.875rem;
					color: var(--bs-gray-700);
					font-weight: 500;
				}

				.tip-box {
					margin-top: 1.5rem;
					padding: 1rem 1.25rem;
					border-radius: 12px;
					background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
					border-left: 4px solid #ffc107;
					display: flex;
					align-items: flex-start;
					gap: 0.75rem;
				}

				.tip-icon {
					font-size: 1.25rem;
					color: #ff9800;
					margin-top: 0.125rem;
					display: block !important;
					visibility: visible !important;
				}

				.tip-icon svg {
					display: block !important;
					visibility: visible !important;
					width: 1.25rem !important;
					height: 1.25rem !important;
				}

				.tip-content {
					flex: 1;
				}

				.tip-title {
					font-size: 0.875rem;
					font-weight: 600;
					color: #856404;
					margin-bottom: 0.25rem;
				}

				.tip-text {
					font-size: 0.8125rem;
					color: #856404;
					line-height: 1.5;
				}

				.start-guide {
					display: flex;
					flex-direction: column;
					gap: 1.25rem;
				}

				.start-step {
					display: flex;
					align-items: flex-start;
					gap: 1rem;
					padding: 1.25rem;
					border-radius: 12px;
					background: var(--bs-gray-50);
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					position: relative;
					border: 1.5px solid transparent;
				}

				.start-step:hover {
					background: var(--bs-gray-100);
					transform: translateX(4px);
					border-color: var(--app-theme-primary);
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.1);
				}

				.start-step:nth-child(1) {
					animation: tourFeatureSlide 0.5s ease-out 0.4s both;
				}

				.start-step:nth-child(2) {
					animation: tourFeatureSlide 0.5s ease-out 0.5s both;
				}

				.start-step:nth-child(3) {
					animation: tourFeatureSlide 0.5s ease-out 0.6s both;
				}

				.start-step::before {
					content: '';
					position: absolute;
					left: 0;
					top: 0;
					bottom: 0;
					width: 3px;
					background: linear-gradient(180deg, var(--bs-primary) 0%, var(--bs-primary-dark, #0056b3) 100%);
					border-radius: 0 2px 2px 0;
				}

				.step-number {
					width: 36px;
					height: 36px;
					border-radius: 10px;
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.15) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.15) 100%);
					border: 2px solid rgba(var(--app-theme-primary-rgb), 0.25);
					display: flex;
					align-items: center;
					justify-content: center;
					color: var(--app-theme-primary);
					font-weight: 600;
					font-size: 1rem;
					flex-shrink: 0;
					box-shadow: 0 2px 8px rgba(var(--app-theme-primary-rgb), 0.1);
					transition: all 0.3s ease;
					position: relative;
				}

				.start-step:hover .step-number {
					transform: scale(1.1) rotate(5deg);
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.25) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.25) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.4);
					box-shadow: 0 4px 12px rgba(var(--app-theme-primary-rgb), 0.2);
				}

				.step-number::after {
					content: '';
					position: absolute;
					inset: -2px;
					border-radius: 12px;
					padding: 2px;
					background: linear-gradient(135deg, var(--app-theme-primary) 0%, var(--app-theme-primary-hover) 100%);
					-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
					-webkit-mask-composite: xor;
					mask-composite: exclude;
					opacity: 0;
					transition: opacity 0.3s ease;
				}

				.start-step:hover .step-number::after {
					opacity: 1;
				}

				.step-content {
					flex: 1;
					padding-top: 0.25rem;
				}

				.step-title {
					font-size: 0.9375rem;
					font-weight: 600;
					color: var(--bs-gray-900);
					margin-bottom: 0.25rem;
				}

				.step-desc {
					font-size: 0.875rem;
					color: var(--bs-gray-600);
					line-height: 1.5;
				}

				.highlight-box {
					margin-top: 1.5rem;
					padding: 1rem 1.25rem;
					border-radius: 12px;
					background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
					border-left: 4px solid var(--bs-primary);
					display: flex;
					align-items: center;
					gap: 0.75rem;
				}

				.highlight-icon {
					font-size: 1.25rem;
					color: var(--app-theme-primary);
					flex-shrink: 0;
					display: block !important;
					visibility: visible !important;
				}

				.highlight-icon svg {
					display: block !important;
					visibility: visible !important;
					width: 1.25rem !important;
					height: 1.25rem !important;
				}

				.highlight-text {
					font-size: 0.875rem;
					color: #1565c0;
					font-weight: 500;
					line-height: 1.5;
				}

				.step-indicator {
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					position: relative;
					overflow: visible;
				}

				.step-indicator::before {
					content: '';
					position: absolute;
					inset: -2px;
					border-radius: 6px;
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.3) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.3) 100%);
					opacity: 0;
					transition: opacity 0.3s ease;
					z-index: -1;
				}

				.step-indicator::after {
					content: '';
					position: absolute;
					inset: -4px;
					border-radius: 8px;
					background: radial-gradient(circle, rgba(var(--app-theme-primary-rgb), 0.4) 0%, transparent 70%);
					opacity: 0;
					transition: opacity 0.3s ease;
					z-index: -2;
				}

				.step-indicator:hover {
					background: linear-gradient(135deg, var(--app-theme-primary-light) 0%, var(--app-theme-primary) 50%, var(--app-theme-primary-hover) 100%) !important;
					border-color: rgba(var(--app-theme-primary-rgb), 0.6) !important;
					box-shadow: 0 3px 12px rgba(var(--app-theme-primary-rgb), 0.5), 0 0 16px rgba(var(--app-theme-primary-rgb), 0.3) !important;
					transform: scaleY(1.3) scaleX(1.1);
				}

				.step-indicator:hover::before {
					opacity: 0.6;
				}

				.step-indicator:hover::after {
					opacity: 0.4;
				}

				[data-bs-theme='dark'] .tour-modal-header {
					background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
				}

				[data-bs-theme='dark'] .tour-title {
					background: linear-gradient(135deg, var(--app-theme-primary) 0%, var(--app-theme-primary-light) 100%);
					-webkit-background-clip: text;
					-webkit-text-fill-color: transparent;
					background-clip: text;
				}

				[data-bs-theme='dark'] .tour-subtitle {
					color: var(--bs-gray-400);
				}

				[data-bs-theme='dark'] .feature-item {
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.08) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.08) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.15);
				}

				[data-bs-theme='dark'] .feature-item:hover {
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.15) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.15) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.3);
				}

				[data-bs-theme='dark'] .feature-title {
					color: var(--bs-gray-100);
				}

				[data-bs-theme='dark'] .feature-desc {
					color: var(--bs-gray-400);
				}

				[data-bs-theme='dark'] .keyboard-item {
					background: var(--bs-gray-800);
				}

				[data-bs-theme='dark'] .keyboard-item:hover {
					background: var(--bs-gray-700);
				}

				[data-bs-theme='dark'] .keyboard-keys kbd {
					background: var(--bs-gray-700);
					border-color: var(--bs-gray-600);
					color: var(--bs-gray-200);
				}

				[data-bs-theme='dark'] .keyboard-desc {
					color: var(--bs-gray-300);
				}

				[data-bs-theme='dark'] .start-step {
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.08) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.08) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.15);
				}

				[data-bs-theme='dark'] .start-step:hover {
					background: linear-gradient(135deg, rgba(var(--app-theme-primary-rgb), 0.15) 0%, rgba(var(--app-theme-primary-hover-rgb), 0.15) 100%);
					border-color: rgba(var(--app-theme-primary-rgb), 0.3);
				}

				[data-bs-theme='dark'] .step-title {
					color: var(--bs-gray-100);
				}

				[data-bs-theme='dark'] .step-desc {
					color: var(--bs-gray-400);
				}

				[data-bs-theme='dark'] .tour-progress-bar {
					background: var(--bs-gray-700);
				}

				[data-bs-theme='dark'] .tour-progress-text {
					color: var(--bs-gray-400);
				}

				[data-bs-theme='dark'] .keyboard-shortcut {
					background: linear-gradient(135deg, var(--bs-gray-700) 0%, var(--bs-gray-600) 100%);
					border-color: var(--bs-gray-600);
					color: var(--bs-gray-200);
				}

				[data-bs-theme='dark'] .keyboard-shortcut:hover {
					background: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-primary-dark, #0056b3) 100%);
					color: white;
				}

				[data-bs-theme='dark'] .step-indicator {
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5) !important;
				}

				/* 深色模式下未激活的步骤指示器 - 提高可见度 */
				[data-bs-theme='dark'] .step-indicator:not([data-current='true']):not(:hover) {
					background: rgba(var(--app-theme-primary-rgb), 0.5) !important;
					border-color: rgba(var(--app-theme-primary-rgb), 0.6) !important;
					box-shadow: 0 1px 4px rgba(var(--app-theme-primary-rgb), 0.4), 0 0 8px rgba(var(--app-theme-primary-rgb), 0.2) !important;
				}

				/* 深色模式下当前步骤指示器 - 更亮的颜色 */
				[data-bs-theme='dark'] .step-indicator[data-current='true'] {
					background: linear-gradient(135deg, var(--app-theme-primary-light) 0%, var(--app-theme-primary) 50%, var(--app-theme-primary-hover) 100%) !important;
					border-color: rgba(var(--app-theme-primary-rgb), 0.7) !important;
					box-shadow: 0 2px 10px rgba(var(--app-theme-primary-rgb), 0.6), 0 0 18px rgba(var(--app-theme-primary-rgb), 0.4) !important;
				}

				[data-bs-theme='dark'] .step-indicator:hover {
					background: linear-gradient(135deg, var(--app-theme-primary-light) 0%, var(--app-theme-primary) 50%, var(--app-theme-primary-hover) 100%) !important;
					border-color: rgba(var(--app-theme-primary-rgb), 0.8) !important;
					box-shadow: 0 3px 12px rgba(var(--app-theme-primary-rgb), 0.7), 0 0 20px rgba(var(--app-theme-primary-rgb), 0.5) !important;
				}

				/* 响应式设计 */
				@media (max-width: 768px) {
					.search-tour-modal .modal-dialog {
						margin: 1rem;
					}

					.tour-modal-header {
						padding: 1.5rem 1.5rem 1rem !important;
					}

					.tour-modal-body {
						padding: 0 1.5rem 1rem !important;
					}

					.tour-modal-footer {
						padding: 1rem 1.5rem 1.5rem !important;
					}

					.tour-title {
						font-size: 1.25rem;
					}

					.tour-icon-wrapper {
						width: 48px;
						height: 48px;
					}

					.tour-icon {
						font-size: 20px;
					}

					.feature-item,
					.keyboard-item,
					.start-step {
						padding: 0.875rem;
					}

					.tour-nav-btn {
						padding: 0.5rem 1rem !important;
						font-size: 0.875rem;
					}
				}

				/* 流畅的线条装饰 */
				.tour-modal-header::after {
					content: '';
					position: absolute;
					bottom: 0;
					left: 2rem;
					right: 2rem;
					height: 1px;
					background: linear-gradient(90deg, transparent 0%, var(--bs-gray-300) 50%, transparent 100%);
				}

				.tour-modal-footer::before {
					content: '';
					position: absolute;
					top: 0;
					left: 2rem;
					right: 2rem;
					height: 1px;
					background: linear-gradient(90deg, transparent 0%, var(--bs-gray-300) 50%, transparent 100%);
				}

				[data-bs-theme='dark'] .tour-modal-header::after,
				[data-bs-theme='dark'] .tour-modal-footer::before {
					background: linear-gradient(90deg, transparent 0%, var(--bs-gray-700) 50%, transparent 100%);
				}

				/* 微妙的脉冲效果 */
				.tour-icon-wrapper::after {
					content: '';
					position: absolute;
					inset: -4px;
					border-radius: 18px;
					background: var(--bs-primary);
					opacity: 0;
					animation: tourIconPulseRing 2s ease-in-out infinite;
				}

				@keyframes tourIconPulseRing {
					0% {
						opacity: 0;
						transform: scale(0.8);
					}
					50% {
						opacity: 0.3;
						transform: scale(1.1);
					}
					100% {
						opacity: 0;
						transform: scale(1.2);
					}
				}
			`}</style>
		</>
	);
});

SearchTourGuide.displayName = 'SearchTourGuide';

export default SearchTourGuide;
